import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import useNotification from "../../hooks/useNotification";
import loading from "../../hooks/useLoading";
import useApi from "../../hooks/useApi";

import PermissionsContext from "../../contexts/permissions";

import api from "../../services/api";

import IUser from "../../interfaces/user";

import configErrors from "../../config/errors/user";

interface User {
    password?: string;
    username?: string;
    email?: string;
    access?: any;
}

const useUser = () => {
    const history = useHistory();

    const { id } = useParams<{ id: string }>();

    const { success, error, confirm } = useNotification();

    const { getOptions } = useApi();

    const { permissions } = useContext(PermissionsContext);

    const initialUser = useCallback(
        () => ({
            password: "",
            username: "",
            email: "",
            access: null,
        }),
        []
    );

    const initialErrors = useCallback(
        () => ({
            password: "",
            username: "",
            email: "",
            access: "",
        }),
        []
    );

    const [user, setUser] = useState<User>(initialUser());

    const [errors, setErrors] = useState(initialErrors());

    const show = useCallback(() => {
        const show = async () => {
            const idLoading = loading();

            const user: IUser = await api.get(`/users/${id}`, getOptions());

            const { name, username, email, role } = user;

            const serializedUser = {
                id,
                name,
                username,
                email,
                access: { value: role.id, label: role.name },
            };

            setUser(serializedUser);

            loading(idLoading);
        };

        show();
    }, [getOptions, id]);

    useEffect(() => {
        if (id) show();
    }, [id, show]);

    const handlerOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        onChange(name, value);
    };

    const onChange = (name: string, value: any) => {
        setUser({ ...user, [name]: value });
    };

    const handlerValidate = (params: {
        update: boolean;
    }) => {
        const { update = false } = params || {};
        let response = true;
        let errors = initialErrors();

        if (!update && !user.password) {
            response = false;
            errors.password = "Preencha esse campo";
        }

        if (!user.username) {
            response = false;
            errors.username = "Preencha esse campo";
        }

        if (!user.access) {
            response = false;
            errors.access = "Preencha esse campo";
        }

        setErrors(errors);

        return response;
    };

    const create = async () => {
        const { username, password, email, access } = user;


        const serializedUser = {
            username,
            password,
            email,
            role: access.value,
        };

        await api.post("/users", serializedUser, getOptions());

        success("Usuário cadastrado");
    };

    const update = async () => {
        const { username, password, email, access } = user;

        const serializedUser = {
            username,
            password,
            email,
            role: access.value,
        };

        await api.put(`/users/${id}`, serializedUser, getOptions());

        success("Usuário editado");
    };

    const handlerOnSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const idLoading = loading();

        try {
            if (!handlerValidate({ update: !!id })) {
                loading(idLoading);
                return;
            }

            if (!id) {
                await create();
            } else {
                await update();
            }

            loading(idLoading);

            history.push("/users");
        } catch (err: any) {
            error("Cadastrar usuário falhou", configErrors, err);

            loading(idLoading);
        }
    };

    const hasUserDestroy = useCallback(() => {
        const {
            findone: { enabled: findone },
            destroy: { enabled: destroy },
        } = permissions["users-permissions"].controllers.user;

        return findone && destroy;
    }, [permissions]);

    const handlerRemove = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        event.preventDefault();

        confirm(
            "Tem certeza que deseja remover o usuário?",
            "Uma vez excluído, você não poderá recuperar!",
            async () => {
                const idLoading = loading();
                try {
                    await api.delete(`/users/${id}`, getOptions());

                    success("Usuário removido");

                    loading(idLoading);

                    history.push("/users");
                } catch (err) {
                    error("Remover usuário falhou");

                    loading(idLoading);
                }
            }
        );
    };

    return {
        user,
        errors,
        handlerOnChange,
        onChange,
        handlerOnSubmit,
        hasUserDestroy,
        handlerRemove,
    };
};

export default useUser;
